import { GLOBAL_CONFIG } from "@/global-config";
import { t } from "@/locales/i18n";
import userStore from "@/store/userStore";
import axios, { type AxiosRequestConfig, type AxiosError, type AxiosResponse } from "axios";
import { toast } from "sonner";
import type { Result } from "#/api";
import { ResultStuts } from "#/enum";

const axiosInstance = axios.create({
	baseURL: GLOBAL_CONFIG.apiBaseUrl,
	timeout: 50000,
	headers: { "Content-Type": "application/json;charset=utf-8" },
});

axiosInstance.interceptors.request.use(
	(config) => {
		console.log('🚀 [API] Request starting:', {
			method: config.method?.toUpperCase(),
			url: config.url,
			baseURL: config.baseURL,
			fullURL: `${config.baseURL}${config.url}`,
		});
		
		// 检查登录是否过期
		const checkLoginExpiry = userStore.getState().actions.checkLoginExpiry;
		const isLoginValid = checkLoginExpiry();
		const userToken = userStore.getState().userToken;
		
		console.log('🔐 [API] Auth check:', {
			isLoginValid,
			hasToken: !!userToken?.accessToken,
		});
		
		// 如果登录已过期，直接跳转到登录页
		if (!isLoginValid && userToken?.accessToken) {
			console.warn('⚠️ [API] Login session expired, redirecting to login page');
			window.location.href = "/login";
			return Promise.reject(new Error("Login session expired"));
		}
		
		// 从userStore获取accessToken
		if (userToken?.accessToken) {
			config.headers.Authorization = `Bearer ${userToken.accessToken}`;
			console.log('✅ [API] Authorization header added');
		} else {
			console.warn('⚠️ [API] No access token found');
		}
		
		return config;
	},
	(error) => {
		console.error('❌ [API] Request interceptor error:', error);
		return Promise.reject(error);
	},
);

axiosInstance.interceptors.response.use(
	(res: AxiosResponse<Result<any>>) => {
		console.log('📦 [API] Response received:', {
			url: res.config.url,
			status: res.status,
			code: res.data?.code,
			dataKeys: res.data ? Object.keys(res.data) : [],
		});
		
		if (!res.data) {
			console.error('❌ [API] No data in response');
			throw new Error(t("sys.api.apiRequestFailed"));
		}
		
		const { code, status, message } = res.data;

		// 支持新的 API 格式（code: 200, 201）和旧的格式（status: 0）
		// 201 是 HTTP 标准中用于创建资源的成功状态码
		const isSuccess = code === 200 || code === 201 || status === ResultStuts.SUCCESS;

		if (!isSuccess) {
			console.error('❌ [API] Request failed:', { code, status, message });
			
			// 特殊处理认证错误 (code: 800)
			if (code === 800) {
				console.warn('⚠️ [API] Authentication error, redirecting to login');
				// Token相关错误，清除用户信息并重定向到登录页
				toast.error(message || "Authentication failed", { position: "top-center" });
				userStore.getState().actions.clearUserInfoAndToken();
				window.location.href = "/login";
				throw new Error(message || "Authentication failed");
			}
			throw new Error(message || t("sys.api.apiRequestFailed"));
		}

		console.log('✅ [API] Request successful');
		return res;
	},
	(error: AxiosError<Result>) => {
		const { response, message } = error || {};
		const errMsg = response?.data?.message || message || t("sys.api.errorMessage");

		console.error('❌ [API] Response error:', {
			status: response?.status,
			code: response?.data?.code,
			message: errMsg,
			url: error.config?.url,
		});

		// 处理HTTP 401状态码和code: 800的认证错误
		if (response?.status === 401 || response?.data?.code === 800) {
			console.warn('⚠️ [API] Authentication error, redirecting to login');
			// 认证相关错误，显示英文错误信息
			const authErrorMsg = response?.data?.message || "Authentication failed";
			toast.error(authErrorMsg, { position: "top-center" });
			userStore.getState().actions.clearUserInfoAndToken();
			window.location.href = "/login";
		} else {
			toast.error(errMsg, { position: "top-center" });
		}

		return Promise.reject(error);
	},
);

class APIClient {
	get<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "GET" });
	}
	post<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "POST" });
	}
	put<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "PUT" });
	}
	delete<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "DELETE" });
	}
	request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return axiosInstance.request<any, AxiosResponse<Result<T>>>(config).then((res) => res.data as T);
	}
}

export default new APIClient();
